import { useMemo } from "react";
import type { Department } from "../contexts/AuthProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { type Nurse, RemoveUnionStaffWarning } from "../utils/models";

/**
 * Whether removing this staff member from a shift should warn about union rules, per the department's
 * `removeUnionStaffWarning` setting: never, only for a recurring (master-schedule) shift, or always.
 * An unknown staff member (`undefined`) or a non-union one never warns.
 */
export const shouldWarnRemovingUnionStaff = (
	nurse: Pick<Nurse, "union"> | undefined,
	department: Pick<Department, "removeUnionStaffWarning">,
	isRecurringShift: boolean,
): boolean =>
	!!nurse?.union &&
	(department.removeUnionStaffWarning === RemoveUnionStaffWarning.All ||
		(department.removeUnionStaffWarning === RemoveUnionStaffWarning.OnlyRecurring && isRecurringShift));

export const useShouldWarnRemovingUnionStaff = ({ nurseId, isRecurringShift }: { nurseId: number; isRecurringShift?: boolean }) => {
	const { chosenDepartment, getNurseById } = useHospitalData();
	const { removeUnionStaffWarning } = chosenDepartment;
	return useMemo(
		() => shouldWarnRemovingUnionStaff(getNurseById(nurseId), { removeUnionStaffWarning }, !!isRecurringShift),
		[removeUnionStaffWarning, getNurseById, nurseId, isRecurringShift],
	);
};
